import { computed, ref, watch } from "vue";

// Import questionnaire data
import questionnaireData from "@/data/questionnaire.json";
import type { QuestionAnswer, Questionnaire, QuestionnaireResponse } from "@/types";

const STORAGE_KEY = "questionnaire_response";

export function useQuestionnaire() {
  const questionnaire = computed(() => questionnaireData as Questionnaire);
  const response = ref<QuestionnaireResponse>({
    version: questionnaireData.version,
    answers: {}
  });

  const requiredQuestionIds = computed(() => {
    return questionnaire.value.sections.flatMap((section) =>
      section.questions.filter((q) => q.required && isQuestionVisible(q.id)).map((q) => q.id)
    );
  });

  const unansweredRequiredQuestionIds = computed(() => {
    return requiredQuestionIds.value.filter((questionId) => !isQuestionAnswered(questionId));
  });

  const validationErrors = ref(new Set<string>());

  function isQuestionVisible(questionId: string) {
    const question = questionnaire.value.sections
      .flatMap((section) => section.questions)
      .find((q) => q.id === questionId);

    if (!question?.showWhen) {
      return true;
    }

    const conditionAnswer = getQuestionAnswer(question.showWhen.questionId);

    return conditionAnswer === question.showWhen.value;
  }

  function isQuestionAnswered(questionId: string) {
    const answer = response.value.answers[questionId];

    if (answer === undefined || answer === "") {
      return false;
    }

    // Handle empty arrays (for checkbox questions)
    if (Array.isArray(answer) && answer.length === 0) {
      return false;
    }

    return true;
  }

  function getQuestionAnswer(questionId: string) {
    return response.value.answers[questionId] ?? null;
  }

  function setQuestionAnswer(questionId: string, value: QuestionAnswer) {
    response.value.answers[questionId] = value;

    if (validationErrors.value.has(questionId)) {
      validationErrors.value.delete(questionId);
    }
  }

  function hasValidationError(questionId: string) {
    return validationErrors.value.has(questionId);
  }

  function validateResponse() {
    validationErrors.value = new Set(unansweredRequiredQuestionIds.value);

    return validationErrors.value.size === 0;
  }

  function saveResponse() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(response.value));
      console.log("Saving questionnaire response:", response.value);
    } catch (error) {
      console.warn("Failed to save response to session storage:", error);
    }
  }

  function loadResponse() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);

      if (!stored) {
        return;
      }

      const dataToLoad = JSON.parse(stored) as QuestionnaireResponse;

      // Check version compatibility
      if (dataToLoad.version !== questionnaire.value.version) {
        console.warn(
          `Response version ${dataToLoad.version} does not match questionnaire version ${questionnaire.value.version}. Discarding incompatible response.`
        );
        return;
      }

      response.value = {
        version: dataToLoad.version,
        answers: dataToLoad.answers
      };
    } catch (error) {
      console.warn("Failed to load response from session storage:", error);
    }
  }

  // Load from storage on initialization
  loadResponse();

  watch(response, saveResponse, { deep: true });

  return {
    questionnaire,
    response,
    unansweredRequiredQuestionIds,
    isQuestionVisible,
    getQuestionAnswer,
    setQuestionAnswer,
    validateResponse,
    hasValidationError
  };
}
